import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/Button';
import { FieldDefinition, EntityDefinition, DataItem } from '@/types/crud';
import { useLanguage } from '@/state/language/LanguageProvider';
import { RichTextEditor } from './RichTextEditor';
import { handleAccessControlErrorInComponent } from '@/services/accessControlService';
import { toast } from 'react-hot-toast';

interface DynamicFormProps {
  fields?: FieldDefinition[];
  entity?: EntityDefinition;
  initialData?: Partial<DataItem>;
  onSubmit: (data: Partial<DataItem>) => Promise<void>;
  onCancel?: () => void;
  submitLabel?: string;
  loading?: boolean;
  dynamicOptions?: Record<string, unknown[]>;
  isEditing?: boolean;
  extraFields?: React.ReactNode;
}

export function DynamicForm({
  fields,
  entity,
  initialData = {},
  onSubmit,
  onCancel,
  submitLabel = 'Save',
  loading = false,
  dynamicOptions = {},
  isEditing = false, // Default to false
  extraFields
}: DynamicFormProps) {
  const [formData, setFormData] = useState<Partial<DataItem>>(initialData || {});
  const [errors, setErrors] = useState<Array<{ field: string; message: string }>>([]);
  const { t } = useLanguage();
  
  // Ref to track if form has been initialized to prevent infinite loops
  const initializedRef = useRef(false);

  // Extract fields from entity if not provided directly - memoize to prevent re-renders
  const entityFields = useMemo(() => fields || entity?.fields || [], [fields, entity?.fields]);
  
  // Sort fields by order if specified
  const formFields = useMemo(() => 
    entityFields.filter((field: FieldDefinition) => !field.hidden && !field.formHidden), 
    [entityFields]
  );
  const sortedFields = useMemo(() => 
    [...formFields].sort((a: FieldDefinition, b: FieldDefinition) => (a.order || 0) - (b.order || 0)),
    [formFields]
  );

  useEffect(() => {
    // Guard against redundant updates to prevent infinite loops
    if (initializedRef.current) return;

    if (initialData && Object.keys(initialData).length > 0) {
      setFormData(initialData);
    } else {
      // Initialize with default values for new records
      const defaultData: Partial<DataItem> = {};
      entityFields.forEach((field: FieldDefinition) => {
        if (field.defaultValue !== undefined && (field.key || field.name)) {
          defaultData[field.key || field.name || ''] = field.defaultValue;
        }
      });
      setFormData(defaultData);
    }

    initializedRef.current = true;
  }, [initialData, entityFields]);

  const handleChange = (fieldKey: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [fieldKey]: value }));
    // Clear error for this field
    setErrors(prev => prev.filter(error => error.field !== fieldKey));
  };

  const validateField = (field: FieldDefinition, value: any): string | null => {
    if (field.required && (!value || value === '')) {
      return t('forms.fieldRequired');
    }

    if (field.pattern && typeof value === 'string' && !new RegExp(field.pattern).test(value)) {
      return t('forms.invalidFormat');
    }

    if (field.validation?.minLength && value && typeof value === 'string' && value.length < field.validation.minLength) {
      return t('forms.minLength', { min: field.validation.minLength });
    }

    if (field.validation?.maxLength && value && typeof value === 'string' && value.length > field.validation.maxLength) {
      return t('forms.maxLength', { max: field.validation.maxLength });
    }

    if (field.minValue !== undefined && value && parseFloat(value) < field.minValue) {
      return t('forms.minValue', { min: field.minValue });
    }

    if (field.maxValue !== undefined && value && parseFloat(value) > field.maxValue) {
      return t('forms.maxValue', { max: field.maxValue });
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all fields
    const newErrors: Array<{ field: string; message: string }> = [];
    
    sortedFields.forEach(field => {
      const fieldKey = field.key || field.name || '';
      const error = validateField(field, (formData || {})[fieldKey]);
      if (error) {
        newErrors.push({ field: fieldKey, message: error });
      }
    });

    if (newErrors.length > 0) {
      setErrors(newErrors);
      toast.error(newErrors[0]?.message || t('forms.invalidFormat'));
      return;
    }

    try {
      const safeFormData = formData || {};
      
      // Filter out fields that shouldn't be sent to the backend
      const filteredFormData = { ...safeFormData };
      
      // Remove fields that are marked as formHidden or are computed fields
      sortedFields.forEach(field => {
        const fieldKey = field.key || field.name || '';
        if (field.formHidden || fieldKey === 'created_at' || fieldKey === 'updated_at') {
          delete filteredFormData[fieldKey];
        }
      });
      
      await onSubmit(filteredFormData);
    } catch (error) {
      console.error('Form submission error:', error);
      handleAccessControlErrorInComponent(error);
    }
  };

  const renderField = (field: FieldDefinition) => {
    // Hide fields that are marked as hidden for forms
    if (field.hidden || field.formHidden) {
      return null;
    }

    // Determine if field should span full width
    // Full width fields span all columns: 1 on mobile, 2 on md, 4 on lg
    const isFullWidth = field.type === 'textarea' || field.type === 'html' || field.fullWidth;
    const fieldClassName = isFullWidth ? 'col-span-1 md:col-span-2 lg:col-span-4' : '';

    // Determine if the field should be read-only
    const isReadOnly = typeof field.readOnly === 'function' 
      ? field.readOnly(isEditing) 
      : field.readOnly || false;

    switch (field.type) {
             case 'text':
       case 'email':
       case 'url':
       case 'phone':
         return (
           <div key={field.key} className={`mb-3 ${fieldClassName}`}>
             <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
               {t(field.label)} {field.required && <span className="text-red-500">*</span>}
             </label>
             {field.helpText && (
               <p className="text-xs text-[var(--color-secondary-text)] mb-2 italic">
                 {t(field.helpText)}
               </p>
             )}
                           <input
                type={field.type}
                value={formData[field.key || field.name || ''] || ''}
                onChange={(e) => handleChange(field.key || field.name || '', e.target.value)}
                placeholder={field.placeholder ? t(field.placeholder) : ''}
                disabled={field.disabled || loading || isReadOnly}
                readOnly={isReadOnly}
                className={`w-full px-2 py-1.5 border rounded-md shadow-sm focus:outline-none focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)] ${
                  errors.find(e => e.field === field.key)?.message ? 'border-red-500' : ''
                } ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              />
             {errors.find(e => e.field === field.key)?.message && <p className="mt-1 text-sm text-red-600">{errors.find(e => e.field === field.key)?.message}</p>}
           </div>
         );

             case 'textarea':
        return (
          <div key={field.key} className={`mb-3 ${fieldClassName}`}>
            <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
              {t(field.label)} {field.required && <span className="text-red-500">*</span>}
            </label>
            {field.helpText && (
              <p className="text-xs text-[var(--color-secondary-text)] mb-2 italic">
                {t(field.helpText)}
              </p>
            )}
                                                      <textarea
                value={formData[field.key || field.name || ''] || ''}
                onChange={(e) => handleChange(field.key || field.name || '', e.target.value)}
                placeholder={field.placeholder ? t(field.placeholder) : ''}
                disabled={field.disabled || loading || isReadOnly}
                readOnly={isReadOnly}
                className={`w-full px-2 py-1.5 border rounded-md shadow-sm focus:outline-none focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)] ${
                  errors.find(e => e.field === field.key)?.message ? 'border-red-500' : ''
                } ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                rows={field.rows || 2}
              />
            {errors.find(e => e.field === field.key)?.message && <p className="mt-1 text-sm text-red-600">{errors.find(e => e.field === field.key)?.message}</p>}
          </div>
        );

            case 'html':
        return (
          <div key={field.key} className={`mb-3 ${fieldClassName}`}>
            <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
              {t(field.label)} {field.required && <span className="text-red-500">*</span>}
            </label>
            {field.helpText && (
              <p className="text-xs text-[var(--color-secondary-text)] mb-2 italic">
                {t(field.helpText)}
              </p>
            )}
            <RichTextEditor
              value={formData[field.key || field.name || ''] || ''}
              onChange={(value) => handleChange(field.key || field.name || '', value)}
              placeholder={field.placeholder ? t(field.placeholder) : ''}
              disabled={field.disabled || loading || isReadOnly}
              readOnly={isReadOnly}
              className={errors.find(e => e.field === field.key)?.message ? 'border-red-500' : ''}
            />
            {errors.find(e => e.field === field.key)?.message && <p className="mt-1 text-sm text-red-600">{errors.find(e => e.field === field.key)?.message}</p>}
          </div>
        );

                           case 'number':
          return (
            <div key={field.key} className={`mb-3 ${fieldClassName}`}>
              <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                {t(field.label)} {field.required && <span className="text-red-500">*</span>}
              </label>
                            <input
                 type="number"
                 value={formData[field.key || field.name || ''] || ''}
                 onChange={(e) => handleChange(field.key || field.name || '', parseFloat(e.target.value) || 0)}
                 placeholder={field.placeholder ? t(field.placeholder) : ''}
                disabled={field.disabled || loading || isReadOnly}
                readOnly={isReadOnly}
                className={`w-full px-2 py-1.5 border rounded-md shadow-sm focus:outline-none focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)] ${
                  errors.find(e => e.field === field.key)?.message ? 'border-red-500' : ''
                } ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                min={field.minValue}
                max={field.maxValue}
                step={field.step}
              />
             {errors.find(e => e.field === field.key)?.message && <p className="mt-1 text-sm text-red-600">{errors.find(e => e.field === field.key)?.message}</p>}
           </div>
         );

             case 'date':
         return (
           <div key={field.key} className={`mb-3 ${fieldClassName}`}>
             <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
               {t(field.label)} {field.required && <span className="text-red-500">*</span>}
             </label>
                           <input
                type="date"
                value={formData[field.key || field.name || ''] ? String(formData[field.key || field.name || '']).split('T')[0] : ''}
                onChange={(e) => handleChange(field.key || field.name || '', e.target.value)}
                disabled={field.disabled || loading || isReadOnly}
                readOnly={isReadOnly}
                className={`w-full px-2 py-1.5 border rounded-md shadow-sm focus:outline-none focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)] ${
                  errors.find(e => e.field === field.key)?.message ? 'border-red-500' : ''
                } ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              />
             {errors.find(e => e.field === field.key)?.message && <p className="mt-1 text-sm text-red-600">{errors.find(e => e.field === field.key)?.message}</p>}
           </div>
         );

                          case 'datetime':
         return (
           <div key={field.key} className={`mb-3 ${fieldClassName}`}>
             <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
               {t(field.label)} {field.required && <span className="text-red-500">*</span>}
             </label>
                           <input
                type="datetime-local"
                value={formData[field.key || field.name || '']
                  ? String(formData[field.key || field.name || '']).slice(0, 16)
                  : ''}
                onChange={(e) => handleChange(field.key || field.name || '', e.target.value)}
                disabled={field.disabled || loading || isReadOnly}
                readOnly={isReadOnly}
                className={`w-full px-2 py-1.5 border rounded-md shadow-sm focus:outline-none focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)] ${
                  errors.find(e => e.field === field.key)?.message ? 'border-red-500' : ''
                } ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
              />
             {errors.find(e => e.field === field.key)?.message && <p className="mt-1 text-sm text-red-600">{errors.find(e => e.field === field.key)?.message}</p>}
           </div>
         );

                           case 'select': {
          const options = field.options || dynamicOptions[field.key || field.name || ''] || [];
          return (
            <div key={field.key} className={`mb-3 ${fieldClassName}`}>
              <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                {t(field.label)} {field.required && <span className="text-red-500">*</span>}
              </label>
              {field.helpText && (
                <p className="text-xs text-[var(--color-secondary-text)] mb-2 italic">
                  {t(field.helpText)}
                </p>
              )}
                            <select
                 value={formData[field.key || field.name || ''] || ''}
                 onChange={(e) => handleChange(field.key || field.name || '', e.target.value)}
                 disabled={field.disabled || loading || isReadOnly}
                 className={`w-full px-2 py-1.5 border rounded-md shadow-sm focus:outline-none focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)] ${
                   errors.find(e => e.field === field.key)?.message ? 'border-red-500' : ''
                 } ${isReadOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
               >
                <option value="">{t('forms.select')} {t(field.label)}</option>
                               {options.map((option: any) => (
                  <option key={option.value || option} value={option.value || option}>
                    {option.label ? t(option.label) : String(option)}
                  </option>
                ))}
             </select>
             {errors.find(e => e.field === field.key)?.message && <p className="mt-1 text-sm text-red-600">{errors.find(e => e.field === field.key)?.message}</p>}
           </div>
         );
          }

                                   case 'checkbox':
        case 'boolean':
          return (
            <div key={field.key} className={`mb-3 ${fieldClassName}`}>
              <label className="block text-sm font-medium text-[var(--color-secondary-text)] mb-2">
                {t(field.label)} {field.required && <span className="text-red-500">*</span>}
              </label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={Boolean(formData[field.key || field.name || '']) || false}
                  onChange={(e) => handleChange(field.key || field.name || '', e.target.checked)}
                  disabled={field.disabled || loading || isReadOnly}
                  className="h-4 w-4 text-[var(--color-primary)] focus:ring-[var(--color-primary)] border-[var(--color-border)] rounded"
                />
                <span className="ml-2 text-sm text-[var(--color-secondary-text)]">{t('common.yes')}</span>
              </div>
              {errors.find(e => e.field === field.key)?.message && <p className="mt-1 text-sm text-red-600">{errors.find(e => e.field === field.key)?.message}</p>}
            </div>
          );

      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {sortedFields.map(renderField)}
      </div>
      {extraFields}

      <div className="flex justify-end space-x-3 pt-4 border-t border-[var(--color-border)]">
        {onCancel && (
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
        )}
        <Button
          type="submit"
          disabled={loading}
        >
          {loading ? t('common.saving') : submitLabel}
        </Button>
      </div>
    </form>
  );
} 